import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './WorkshopRegistration.module.css';
import WorkshopPaymentModal from './WorkshopPaymentModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function WorkshopRegistration() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91'
  });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [registrationId, setRegistrationId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    
    // For phone input, only allow digits
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '');
      setFormData({
        ...formData,
        [name]: numericValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceedToPayment = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Register with backend
      const response = await fetch(`${API_URL}/api/workshop/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          workshopDate: 'Saturday, Sunday & Monday (24th, 25th & 26th Jan)',
          workshopTime: '6 PM – 8 PM'
        })
      });

      const data = await response.json();

      if (data.success) {
        setRegistrationId(data.data.registrationId);
        setShowPaymentModal(true);
      } else {
        alert(data.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Failed to register. Please check your internet connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
  };

  const handleRegistrationComplete = async () => {
    try {
      const registrantName = formData.name;
      const registrantEmail = formData.email;
      setFormData({
        name: '',
        email: '',
        phone: '',
        countryCode: '+91'
      });
      setShowPaymentModal(false);
      setRegistrationId(null);
      
      navigate('/', { state: { registrationSuccess: true, registrantName, registrantEmail } });
    } catch (error) {
      console.error('Error completing registration:', error);
    }
  };

  return (
    <div className={styles.registrationPage}>
      <div className={styles.container}>
        {/* Left Side - Workshop Details */}
        <div className={styles.leftPanel}>
          <div className={styles.workshopCard}>
            <div className={styles.workshopHeader}>
              <h2 className={styles.workshopTitle}>💝 Valentine's Special Crochet Workshop</h2>
              <p className={styles.byLine}>By Rusticyarn</p>
              <div className={styles.priceSection}>
                <span className={styles.oldPrice}>₹199</span>
                <span className={styles.newPrice}>₹99</span>
              </div>
            </div>

            <div className={styles.workshopPoster}>
              <div className={styles.posterContent}>
                <div className={styles.posterTitleText}>VALENTINE'S<br/>CROCHET<br/>WORKSHOP</div>
                <div className={styles.posterDateText}>Starting Saturday, 24th Jan</div>
                <div className={styles.posterDetailsText}>
                  📹 LIVE online classes<br/>
                  ⭐ Limited seats
                </div>
                <div className={styles.posterEmojis}>💝🧶💕✨</div>
              </div>
            </div>

            <div className={styles.workshopDetails}>
              <h3>Workshop Details:</h3>
              <p><strong>Dates:</strong> Saturday, Sunday & Monday (24th, 25th & 26th Jan)</p>
              <p><strong>Time:</strong> 6 PM – 8 PM</p>
              <p><strong>Recording:</strong> Yes</p>
              <div className={styles.divider}></div>
              <p className={styles.description}>
                Create beautiful Valentine's Day crochet gifts! Learn step by step and craft heartfelt projects perfect for your loved ones 💝
              </p>
            </div>

            <div className={styles.benefitsSection}>
              <h3>💝 You'll get:</h3>
              <ul>
                <li>🎥 3-Day Live video classes with expert</li>
                <li>📚 Daily guidance & feedback</li>
                <li>💬 Exclusive WhatsApp Group</li>
                <li>🎓 Certificate of completion</li>
                <li>📹 Recorded sessions for review</li>
                <li>💕 Learn to create Valentine's special gifts</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div className={styles.rightPanel}>
          <div className={styles.formCard}>
            <h2 className={styles.formTitle}>Payment details</h2>
            <p className={styles.formSubtitle}>Complete your purchase by providing your payment details.</p>

            <form onSubmit={handleProceedToPayment}>
              <div className={styles.formGroup}>
                <label htmlFor="name">Billing information</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? styles.inputError : ''}
                />
                {errors.name && <span className={styles.errorText}>{errors.name}</span>}
              </div>

              <div className={styles.formGroup}>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? styles.inputError : ''}
                />
                {errors.email && <span className={styles.errorText}>{errors.email}</span>}
              </div>

              <div className={styles.formGroup}>
                <div className={styles.phoneInputGroup}>
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className={styles.countryCode}
                  >
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+61">🇦🇺 +61</option>
                  </select>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="1234567890"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength="10"
                    className={`${styles.phoneInput} ${errors.phone ? styles.inputError : ''}`}
                  />
                </div>
                {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
              </div>

              <div className={styles.priceBreakdown}>
                <div className={styles.serviceRow}>
                  <span>Service</span>
                  <span></span>
                </div>
                <div className={styles.serviceRow}>
                  <span>Valentine's Special Crochet Workshop</span>
                  <span>
                    <span className={styles.strikethrough}>₹199</span> ₹99.00
                  </span>
                </div>
                <div className={styles.divider}></div>
                <div className={styles.totalRow}>
                  <span>Amount to be paid:</span>
                  <span className={styles.totalAmount}>₹99.00</span>
                </div>
              </div>

              <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
              </button>

              <div className={styles.note}>
                <p>⚠️ <strong>Note:</strong> After clicking "Proceed to Payment", you'll see a QR code. Please scan and pay, then upload the payment screenshot to complete your registration.</p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showPaymentModal && registrationId && (
        <WorkshopPaymentModal
          onClose={handleCloseModal}
          onComplete={handleRegistrationComplete}
          registrationData={formData}
          registrationId={registrationId}
        />
      )}
    </div>
  );
}
